import React, { Component } from 'react'
import compose from 'lodash/flowRight'

import {
  SelectComponentWrapper,
  SelectDropdownButton,
  Placeholder,
  Menu,
} from '~/views/components/ui/inputs/v2/Select/shared/components/selectElements'
import {
  ProviderProps,
  withDrawerLockingContext,
} from '~/views/components/WithDrawerLockingContext'

import Downshift from './components/MultiDownshift'

interface MultiSelectProps {
  placeholder: string
  renderValue: (selectedItems: string[]) => void
  fullWidth?: boolean
  required?: boolean
  id: string
  disabled?: boolean
  drawerLockingContext: ProviderProps
  dataTestId?: string

  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  downshiftRef: React.RefObject<any>
  children?: React.ReactNode
}

class MultiSelect extends Component<MultiSelectProps> {
  override state = {
    focused: false,
  }
  input: HTMLInputElement | null = null

  onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = this.props
    this.setState({ focused: true })
    if (onFocus) {
      onFocus(event)
    }
  }

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = this.props
    this.setState({ focused: false })
    if (onBlur) {
      onBlur(event)
    }
  }

  onStateChange = (event: any) => {
    const { drawerLockingContext } = this.props

    if (event.isOpen) {
      drawerLockingContext && drawerLockingContext.disableBackdropClick()
    }
  }

  override render() {
    const {
      children,
      placeholder,
      renderValue,
      fullWidth,
      required,
      id,
      disabled,
      drawerLockingContext,
      downshiftRef,
      dataTestId,
      ...rest
    } = this.props

    return (
      <Downshift
        itemToString={(selected: { name: string } | null) =>
          selected ? selected.name : ''
        }
        onStateChange={this.onStateChange}
        ref={downshiftRef}
        {...rest}
      >
        {({
          getItemProps,
          getMenuProps,
          getRootProps,
          getToggleButtonProps,
          isOpen,
          selectedItems,
        }: Record<string, any>) => {
          return (
            <SelectComponentWrapper {...getRootProps({ refKey: 'innerRef' })}>
              <SelectDropdownButton
                data-testid={`${dataTestId}-select-dropdown`}
                fullWidth={fullWidth}
                focused={this.state.focused}
                disabled={disabled}
                {...getToggleButtonProps({
                  onClick: () => this.input?.focus(),
                  disabled,
                })}
              >
                {selectedItems.length === 0 && (
                  <Placeholder color="#b6b6b6">{placeholder}</Placeholder>
                )}
                {typeof renderValue === 'function'
                  ? renderValue(selectedItems as string[])
                  : (selectedItems as Record<string, string>[])
                      .map((item) => item.name)
                      .join(', ')}
                <input
                  id={id}
                  required={required}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  onKeyDown={(event) => {
                    drawerLockingContext &&
                      drawerLockingContext.handleKeyDown(
                        event as unknown as KeyboardEvent,
                      )
                  }}
                  onChange={() => false}
                  value={selectedItems as string[]}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  ref={(node) => {
                    this.input = node
                  }}
                />
              </SelectDropdownButton>
              {isOpen && (
                <Menu {...getMenuProps({ open: isOpen })}>
                  {React.Children.map(children, (child, index) => {
                    if (!React.isValidElement(child)) {
                      return null
                    }
                    const childElement = child as React.ReactElement<{
                      value: string
                    }>
                    return React.cloneElement(child, {
                      ...getItemProps({
                        item: childElement.props.value,
                        index,
                        isSelected: selectedItems.includes(
                          childElement.props.value,
                        ),
                      }),
                    })
                  })}
                </Menu>
              )}
            </SelectComponentWrapper>
          )
        }}
      </Downshift>
    )
  }
}

export default compose(withDrawerLockingContext)(MultiSelect)
